// @ts-nocheck
/** *****
 * Store Custom Code:
 * For updates, compare with: modules\content-library\src\Widgets\Common\LanguageMenu.tsx
 * ****** */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLanguages } from "@insite/client-framework/Store/Context/ContextSelectors";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
// import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon"; // Store Custom Code
import Globe from "@insite/mobius/Icons/Globe";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

/* Store Custom Code - Start */
import { getCookie } from "@insite/client-framework/Common/Cookies";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps, IconProps } from "@insite/mobius/Icon/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Popover, { PopoverPresentationProps } from "@insite/mobius/Popover/Popover";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import ChevronDown from "@store/Library/Components/Toolkit/Icons/ChevronDown";
import { languageCodeWithoutLocal } from "@store/Framework/Common/Utilities/formatLanguages";
import CheckmarkCircle from "@store/Library/Components/Icons/CheckmarkCircle";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { popoverStyles } from "@store/Themes/common/mixins/popover";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Store Custom Code - End */

const enum fields {
    showIcon = "showIcon",
}

const mapStateToProps = (state: ApplicationState) => ({
    languages: getLanguages(state),
    currentLanguage: state.context.session.language,
    currentMode: getCookie(NavigationModeCookieName) || "Storefront",
});

const mapDispatchToProps = {
    setLanguage,
};

export interface LanguageMenuStyles {
    languageWrapper?: InjectableCss;
    languageIcon?: IconPresentationProps;
    languageSelect?: SelectPresentationProps;
    // Store Custom Code
    currentLanguage?: LinkPresentationProps;
    currentLanguageIcon?: IconProps;
    globeLanguageIcon?: IconPresentationProps;
    popover?: PopoverPresentationProps;
    languageItem?: TypographyProps;
    selectedIcon?: IconPresentationProps;
    languageItemClickable?: ClickableProps;
    selectedLanguageItemClickable?: ClickableProps;
}

const currentLanguageSpacing = 3; // Store Custom Code

export const languageMenuStyles: LanguageMenuStyles = {
    languageWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    /* Store Custom Code - Start */
    globeLanguageIcon: {
        size: 22,
        src: Globe,
        color: "icon",
        css: css`
            margin: 0 ${pxToRem(10)} 0 0;
        `,
    },
    currentLanguage: {
        typographyProps: {
            css: css`
                display: flex;
                align-items: center;
                padding: ${pxToRem(currentLanguageSpacing)} 0 ${pxToRem(currentLanguageSpacing)} ${pxToRem(15)};
            `,
        },
        css: css`
            ${linkStyles("primary", "primary", false, true)}
            height: 100%;
            text-transform: uppercase;
        `,
        mergeCss: true,
    },
    currentLanguageIcon: {
        src: ChevronDown,
        css: css`
            margin-left: ${pxToRem(7)};
        `,
    },
    popover: {
        wrapperProps: {
            css: css`
                height: 100%;
                display: flex;
                align-items: center;
            `,
        },
        contentBodyProps: {
            css: css`
                ${popoverStyles()}
                color: inherit;
                max-height: none;
            `,
        },
    },
    languageItem: {
        css: css`
            white-space: nowrap;
        `,
    },
    languageItemClickable: {
        css: css`
            ${linkStyles("primary", "primary", false, true)}
            display: flex;
            justify-content: space-between;
            padding: ${pxToRem(10)};
            width: 100%;
        `,
    },
    selectedIcon: {
        size: 16,
        src: CheckmarkCircle,
        color: "primary",
        css: css`
            margin-left: ${pxToRem(10)};
        `,
    },
    /* Store Custom Code - End */
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showIcon]: boolean;
    };
}

export const LogoImage = styled.img`
    height: 22px;
`;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
export const LanguageMenu: FC<Props> = ({
    languages,
    currentLanguage,
    fields,
    setLanguage,
    currentMode, // Store Custom Code
}) => {
    if (!languages || languages.length <= 1) {
        return null;
    }

    const styles = useMergeStyles("languageMenu", languageMenuStyles);

    const menuId = "languageMenu";

    const element = React.useRef<HTMLUListElement>(null); // Store Custom Code
    const popover = React.useRef<HTMLUListElement>(null); // Store Custom Code

    /* Store Custom Code - Start */
    const vmiColor = css`
        color: ${getColor("primary.contrast")};
    `;

    const languageLink = (
        <Link {...styles.currentLanguage}>
            {fields.showIcon &&
                (currentLanguage?.imageFilePath ? (
                    <LogoImage src={currentLanguage.imageFilePath} alt="" />
                ) : (
                    <Icon {...styles.globeLanguageIcon} color={currentMode === "Vmi" ? "primary.contrast" : "icon"} />
                ))}
            <StyledWrapper css={currentMode === "Vmi" ? vmiColor : undefined}>
                {currentLanguage ? languageCodeWithoutLocal(currentLanguage.languageCode) : "--"}
            </StyledWrapper>
            <Icon {...styles.currentLanguageIcon} color={currentMode === "Vmi" ? "primary.contrast" : "icon"} />
        </Link>
    );

    return (
        <StyledWrapper {...styles.languageWrapper}>
            {/* Store Custom Code - Removed Hidden Current typography and Select */}
            <Popover
                {...styles.popover}
                contentBodyProps={{
                    ...styles.popover?.contentBodyProps,
                    as: "div",
                    ref: element,
                    css: css`
                        ${styles.popover?.contentBodyProps?.css}
                    `,
                }}
                controlsId="language_selector"
                positionFunction={() => {
                    return {
                        position: "absolute",
                        right: 0,
                        top: "100%",
                    };
                }}
                ref={popover}
                insideRefs={[element]}
                popoverTrigger={languageLink}
            >
                {languages.map(c => (
                    <Clickable
                        key={c.id}
                        {...styles.languageItemClickable}
                        onClick={() => setLanguage({ languageId: c.id })}
                    >
                        <Typography {...styles.languageItem}>{c.description}</Typography>
                        {c.id === currentLanguage?.id && <Icon {...styles.selectedIcon} />}
                    </Clickable>
                ))}
            </Popover>
        </StyledWrapper>
    );
    /* Store Custom Code - End */
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LanguageMenu),
    definition: {
        displayName: "Language Menu",
        icon: "language",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
